import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock, Music, Film, Briefcase, Landmark, ShieldCheck, ArrowLeft, Share2, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` } });

const PERKS = [
  { icon: Clock, title: '8 studio hours / month', text: 'Priority booking with any engineer except Trap Spade (by special request). Up to 4 unused hours roll over.' },
  { icon: Music, title: '2 custom beat leases', text: 'Fresh custom beats made for you every month.' },
  { icon: Film, title: '2 reels or 1 music video', text: 'Content to push your releases.' },
  { icon: Briefcase, title: 'Artist direction', text: 'Release planning, clearances, splits, hosting and rollout help.' },
];

// SAMPLE results - replace with real artist results when available
const SAMPLE_RESULTS = [
  { who: 'Sample Artist A', before: '1 song every few months', after: '4 releases in 90 days', stat: '+320% monthly listeners' },
  { who: 'Sample Artist B', before: 'No visuals, no rollout', after: '2 reels + 1 music video', stat: '+12k reel views' },
  { who: 'Sample Artist C', before: 'Unregistered splits', after: 'Splits + clearances done', stat: 'Ready for distribution' },
];

const SharePlan = () => {
  const link = `${window.location.origin}/artist-plan`;
  const text = 'HQ Studios Artist Plan: 8 studio hours, 2 custom beat leases, 2 reels + artist direction every month for $400.';
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'HQ Studios Artist Plan', text, url: link }); return; } catch { /* fall back to copy */ }
    }
    await navigator.clipboard.writeText(`${text} ${link}`);
    toast.success('Link copied - paste it in your bio or story');
  };
  return (
    <button data-testid="artist-plan-share-btn" onClick={share}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-yellow-400/40 text-yellow-200 hover:bg-yellow-400/10 transition-colors text-sm font-semibold">
      <Share2 size={14} /> Share this plan
    </button>
  );
};

const SampleResults = () => (
  <div data-testid="artist-plan-results" className="mt-12">
    <div className="flex items-center gap-2 mb-1">
      <TrendingUp className="w-5 h-5 text-yellow-300" />
      <h2 className="text-base md:text-lg font-bold">What a plan month looks like</h2>
    </div>
    <p data-testid="artist-plan-sample-label" className="text-xs uppercase tracking-wider text-amber-300/80 mb-4">Sample results - real artist results coming soon</p>
    <div className="grid sm:grid-cols-3 gap-4">
      {SAMPLE_RESULTS.map(r => (
        <div key={r.who} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <p className="text-sm font-bold text-white">{r.who}</p>
          <p className="text-xs text-gray-500 mt-3">Before</p>
          <p className="text-sm text-gray-300">{r.before}</p>
          <p className="text-xs text-gray-500 mt-3">After</p>
          <p className="text-sm text-white">{r.after}</p>
          <p className="text-sm font-bold text-yellow-300 mt-3">{r.stat}</p>
        </div>
      ))}
    </div>
  </div>
);

const MemberPanel = ({ plan, onCancel, cancelling }) => (
  <div data-testid="artist-plan-member-panel" className="rounded-2xl border border-yellow-400/30 bg-white/5 p-6 space-y-5">
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-bold text-white">Your Artist Plan</h2>
      <span data-testid="artist-plan-status" className="text-xs uppercase tracking-wider px-2 py-1 rounded-full bg-yellow-400/15 text-yellow-300">
        {plan.cancel_at_period_end ? 'Ending' : plan.status}
      </span>
    </div>
    {plan.last_payment_status !== 'paid' && (
      <p data-testid="artist-plan-pending" className="text-sm text-amber-300">
        Your bank payment is processing (usually 3-5 business days). Your hours unlock once it clears.
      </p>
    )}
    <div className="grid grid-cols-3 gap-3 text-center">
      <div className="rounded-xl bg-black/40 p-3"><div data-testid="artist-plan-hours" className="text-2xl font-black text-white">{plan.hours_left ?? 0}</div><div className="text-xs text-gray-400">hours left</div></div>
      <div className="rounded-xl bg-black/40 p-3"><div data-testid="artist-plan-leases" className="text-2xl font-black text-white">{plan.beat_leases_left ?? 0}</div><div className="text-xs text-gray-400">beat leases</div></div>
      <div className="rounded-xl bg-black/40 p-3"><div data-testid="artist-plan-reels" className="text-2xl font-black text-white">{plan.music_video_chosen ? 'MV' : (plan.reels_left ?? 0)}</div><div className="text-xs text-gray-400">{plan.music_video_chosen ? 'music video used' : 'reels'}</div></div>
    </div>
    <div className="text-sm text-gray-300 space-y-1">
      <p>Months paid: <span className="text-white font-semibold">{plan.paid_months || 0}</span></p>
      {plan.current_period_end && <p>{plan.cancel_at_period_end ? 'Ends' : 'Renews'}: <span className="text-white font-semibold">{new Date(plan.current_period_end).toLocaleDateString()}</span></p>}
    </div>
    <Link to="/book" data-testid="artist-plan-book-btn" className="block text-center w-full py-3 rounded-full bg-yellow-400 text-black font-bold hover:bg-yellow-300 transition-colors">Book with plan hours</Link>
    {!plan.cancel_at_period_end && (
      <button data-testid="artist-plan-cancel-btn" disabled={cancelling} onClick={onCancel}
        className="w-full text-sm text-gray-400 hover:text-red-300 transition-colors disabled:opacity-50">
        {cancelling ? 'Cancelling...' : 'Cancel plan'}
      </button>
    )}
  </div>
);

export default function ArtistPlan() {
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const loggedIn = !!localStorage.getItem('auth_token');

  const load = () => loggedIn && axios.get(`${API}/subscriptions/me`, authHeader()).then(r => setPlan(r.data)).catch(() => setPlan(null));
  useEffect(() => {
    load();
    if (params.get('status') === 'success') toast.success('Welcome to the Artist Plan! Your bank payment is processing.');
  }, []);

  const join = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/subscriptions/checkout`, { origin_url: window.location.origin, agreed_to_terms: agreed }, authHeader());
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start sign-up');
      setBusy(false);
    }
  };

  const cancel = async () => {
    const early = (plan?.paid_months || 0) < 3;
    const msg = early
      ? 'You are inside the 3-month minimum. Cancelling charges a $200 early-cancellation fee to your bank account. Your plan stays active until the end of this billing month. Continue?'
      : 'Your plan will stay active until the end of this billing month. Continue?';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/subscriptions/cancel`, {}, authHeader());
      toast.success(r.data.message);
      setPlan({ ...plan, ...r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="artist-plan-page" className="min-h-screen bg-[#0b0614] text-white px-5 py-10">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-8"><ArrowLeft size={14} /> Back</Link>
        <p className="text-xs uppercase tracking-[0.3em] text-yellow-300 mb-3">HQ Studios Membership</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">Artist Plan</h1>
        <p className="text-base md:text-lg text-gray-300 mt-3">$400 / month. Everything you need to drop consistently.</p>
        <div className="mt-5"><SharePlan /></div>

        <div className="grid sm:grid-cols-2 gap-4 mt-10">
          {PERKS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-yellow-400/40 transition-colors">
              <Icon className="w-5 h-5 text-yellow-300 mb-3" />
              <h3 className="font-bold">{title}</h3>
              <p className="text-sm text-gray-400 mt-1">{text}</p>
            </div>
          ))}
        </div>

        <SampleResults />

        <div className="mt-10">
          {plan?.has_plan ? (
            <MemberPanel plan={plan} onCancel={cancel} cancelling={busy} />
          ) : (
            <div className="rounded-2xl border border-yellow-400/30 bg-white/5 p-6 space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-300"><Landmark size={16} className="text-yellow-300" /> Paid by bank account (ACH) only. First month is charged when you join.</div>
              <ul className="text-sm text-gray-300 space-y-1.5 list-disc pl-5">
                <li>3-month minimum. Cancelling before 3 paid months has a <strong className="text-white">$200 early-cancellation fee</strong>.</li>
                <li>Billed automatically every month from your bank account.</li>
                <li>Bank payments take 3-5 business days to clear. Your hours unlock once each payment clears.</li>
                <li>Up to 4 unused hours roll over. Beat leases and reels reset every month.</li>
                <li>Trap Spade sessions are by special request only.</li>
              </ul>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input data-testid="artist-plan-terms-checkbox" type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1 accent-yellow-400" />
                <span>I agree to the 3-month minimum, the $200 early-cancellation fee and monthly bank charges.</span>
              </label>
              {loggedIn ? (
                <button data-testid="artist-plan-join-btn" disabled={!agreed || busy} onClick={join}
                  className="w-full py-3 rounded-full bg-yellow-400 text-black font-bold hover:bg-yellow-300 transition-colors disabled:opacity-40">
                  {busy ? 'Opening secure checkout...' : 'Join for $400/month'}
                </button>
              ) : (
                <Link to="/join" data-testid="artist-plan-login-btn" className="block text-center w-full py-3 rounded-full bg-yellow-400 text-black font-bold">Log in or sign up to join</Link>
              )}
              <p className="flex items-center gap-1.5 text-xs text-gray-500"><ShieldCheck size={12} /> Secure bank verification by Stripe.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
